import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { TrashIcon, ExclamationTriangleIcon, StarIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

const AdminUsers = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [userType, setUserType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    user: null,
    deleteProjects: false,
    handleRewards: 'keep',
    forceDelete: false
  });

  const limit = 20;

  // 사용자 목록 조회
  const { data, isLoading } = useQuery(
    ['adminUsers', searchTerm, userType, currentPage],
    async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (userType !== 'all') params.append('user_type', userType);
      params.append('page', currentPage);
      params.append('limit', limit);
      
      const response = await api.get(`/users?${params.toString()}`);
      // 관리자와 소장 제외 (삭제 기능 미구현)
      const filteredUsers = response.data.users?.filter(user => 
        user.user_type !== 'admin' && user.user_type !== 'contractor'
      ) || [];
      
      return {
        ...response.data,
        users: filteredUsers,
        total: filteredUsers.length
      };
    }
  );

  // 일반 사용자 삭제
  const deleteUserMutation = useMutation(
    ({ userId, deleteProjects, forceDelete }) => 
      api.delete(`/delete/user/${userId}`, { 
        data: { deleteProjects, forceDelete } 
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminUsers');
        toast.success('사용자가 삭제되었습니다');
        setDeleteModal({ show: false, user: null, deleteProjects: false, handleRewards: 'keep', forceDelete: false });
      },
      onError: (error) => {
        const errorData = error.response?.data;
        
        if (errorData?.vipInfo) {
          toast.error('VIP 회원은 VIP 삭제 기능을 사용해주세요');
        } else if (errorData?.projects) {
          toast.error(
            <div>
              <p>{errorData.error}</p>
              <p className="text-sm mt-1">
                프로젝트 {errorData.projects.length}개가 있습니다
              </p>
              <p className="text-xs mt-1">프로젝트도 삭제하려면 옵션을 선택하세요</p>
            </div>,
            { duration: 5000 }
          );
        } else {
          toast.error(errorData?.error || '삭제 중 오류가 발생했습니다');
        }
      }
    }
  );

  // VIP 사용자 삭제
  const deleteVipMutation = useMutation(
    ({ userId, deleteProjects, handleRewards, forceDelete }) => 
      api.delete(`/delete/vip/${userId}`, { 
        data: { deleteProjects, handleRewards, forceDelete } 
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminUsers');
        toast.success('VIP 회원이 삭제되었습니다');
        setDeleteModal({ show: false, user: null, deleteProjects: false, handleRewards: 'keep', forceDelete: false });
      },
      onError: (error) => {
        const errorData = error.response?.data;
        
        if (errorData?.pendingWithdrawals) {
          toast.error(
            <div>
              <p>{errorData.error}</p>
              <p className="text-sm mt-1">
                대기중: {errorData.pendingWithdrawals.count}건, 
                {errorData.pendingWithdrawals.amount?.toLocaleString()}원
              </p>
            </div>,
            { duration: 5000 }
          );
          setDeleteModal(prev => ({ ...prev, forceDelete: true }));
        } else if (errorData?.balance) {
          toast.error(
            <div>
              <p>{errorData.error}</p>
              <p className="text-sm mt-1">
                잔액: {errorData.balance?.toLocaleString()}원
              </p>
            </div>,
            { duration: 5000 }
          );
        } else if (errorData?.projects) {
          toast.error(
            <div>
              <p>{errorData.error}</p>
              <p className="text-sm mt-1">
                프로젝트 {errorData.projects.length}개가 있습니다
              </p>
            </div>,
            { duration: 5000 }
          );
        } else {
          toast.error(errorData?.error || '삭제 중 오류가 발생했습니다');
        }
      }
    }
  );

  const handleDelete = () => {
    if (!deleteModal.user) return;
    
    if (deleteModal.user.is_vip) {
      deleteVipMutation.mutate({
        userId: deleteModal.user.id,
        deleteProjects: deleteModal.deleteProjects,
        handleRewards: deleteModal.handleRewards,
        forceDelete: deleteModal.forceDelete
      });
    } else {
      deleteUserMutation.mutate({
        userId: deleteModal.user.id,
        deleteProjects: deleteModal.deleteProjects,
        forceDelete: deleteModal.forceDelete
      });
    }
  };

  const users = data?.users || [];
  const totalPages = data?.pages || 1;

  const getUserTypeLabel = (type) => {
    const labels = {
      admin: '관리자',
      contractor: '소장',
      customer: '고객',
      vip_customer: 'VIP'
    };
    return labels[type] || type;
  };

  const getUserTypeBadgeColor = (type) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      contractor: 'bg-blue-100 text-blue-800',
      customer: 'bg-gray-100 text-gray-800',
      vip_customer: 'bg-yellow-100 text-yellow-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // 검색이나 필터 변경 시 첫 페이지로 리셋
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, userType]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">고객 완전삭제</h1>
        <p className="text-sm text-gray-600 mb-6">* 일반 고객과 VIP 고객만 삭제 가능합니다</p>
        {/* 필터 */}
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            placeholder="사용자 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <select
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">전체</option>
            <option value="customer">고객</option>
            <option value="vip_customer">VIP</option>
          </select>
        </div>

        {/* 사용자 목록 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사용자
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    유형
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    연락처
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    프로젝트
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VIP 정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    가입일
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center">
                          {user.name}
                          {user.is_vip && (
                            <StarIcon className="h-4 w-4 text-yellow-500 ml-1" />
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.username || `#${user.id}`}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getUserTypeBadgeColor(user.user_type)}`}>
                        {getUserTypeLabel(user.user_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.email || '-'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.phone || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.project_count > 0 ? (
                        <div className="text-sm">
                          <div className="text-gray-900">
                            {user.project_count}개
                          </div>
                          <div className="text-gray-500 text-xs truncate max-w-xs" title={user.project_titles}>
                            {user.project_titles || '-'}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.is_vip ? (
                        <div className="text-sm">
                          <div className="text-gray-900">
                            잔액: {user.current_balance?.toLocaleString() || 0}원
                          </div>
                          <div className="text-gray-500 text-xs">
                            총 적립: {user.total_earned?.toLocaleString() || 0}원
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(user.created_at), 'yyyy-MM-dd')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {user.user_type !== 'admin' && (
                        <button
                          onClick={() => setDeleteModal({ 
                            show: true, 
                            user, 
                            deleteProjects: false,
                            handleRewards: 'keep',
                            forceDelete: false 
                          })}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="mt-4 flex justify-center">
            <nav className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이전
              </button>
              
              {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* 삭제 확인 모달 */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                {deleteModal.user?.is_vip ? 'VIP 회원' : '사용자'} 삭제 확인
              </h3>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                다음 사용자를 삭제하시겠습니까?
              </p>
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-medium text-gray-900 flex items-center">
                  {deleteModal.user?.name}
                  {deleteModal.user?.is_vip && (
                    <StarIcon className="h-4 w-4 text-yellow-500 ml-1" />
                  )}
                </p>
                <p className="text-sm text-gray-500">
                  {deleteModal.user?.email || deleteModal.user?.phone || `ID: #${deleteModal.user?.id}`}
                </p>
                {deleteModal.user?.is_vip && deleteModal.user?.current_balance > 0 && (
                  <p className="text-sm text-red-600 mt-1">
                    리워드 잔액: {deleteModal.user.current_balance.toLocaleString()}원
                  </p>
                )}
              </div>
            </div>

            {/* 옵션 */}
            <div className="mb-4 space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={deleteModal.deleteProjects}
                  onChange={(e) => setDeleteModal(prev => ({ 
                    ...prev, 
                    deleteProjects: e.target.checked 
                  }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">
                  관련 프로젝트도 함께 삭제
                </span>
              </label>

              {deleteModal.user?.is_vip && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    리워드 처리 방법:
                  </label>
                  <select
                    value={deleteModal.handleRewards}
                    onChange={(e) => setDeleteModal(prev => ({ 
                      ...prev, 
                      handleRewards: e.target.value 
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="keep">유지 (경고만 표시)</option>
                    <option value="forfeit">포기 (잔액 소멸)</option>
                  </select>
                </div>
              )}

              {deleteModal.forceDelete && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-800">
                    <strong>강제 삭제 모드:</strong> 대기 중인 인출 요청이나 리워드가 있어도 삭제됩니다.
                  </p>
                </div>
              )}
            </div>

            <div className="text-sm text-gray-600 mb-4">
              <p className="font-medium mb-1">삭제되는 데이터:</p>
              <ul className="list-disc list-inside text-xs space-y-1">
                <li>모든 메시지 및 댓글</li>
                <li>업로드한 파일</li>
                <li>알림 설정 및 기기 정보</li>
                {deleteModal.user?.is_vip && (
                  <>
                    <li>리워드 내역 및 인출 요청</li>
                    <li>VIP 회원 정보</li>
                  </>
                )}
                {deleteModal.deleteProjects && <li>생성한 모든 프로젝트</li>}
              </ul>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setDeleteModal({ 
                  show: false, 
                  user: null, 
                  deleteProjects: false,
                  handleRewards: 'keep',
                  forceDelete: false 
                })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteUserMutation.isLoading || deleteVipMutation.isLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  deleteModal.forceDelete 
                    ? 'bg-red-700 hover:bg-red-800' 
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
              >
                {(deleteUserMutation.isLoading || deleteVipMutation.isLoading) ? '삭제 중...' : 
                  deleteModal.forceDelete ? '강제 삭제' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;